import { useState, useCallback } from 'react';
import { createCustomerPortalSession } from '../services/customerPortalService';

interface UseCustomerPortalReturn {
  openPortal: (returnUrl?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Custom hook for managing Stripe Customer Portal sessions
 * Handles loading states, error management, and portal redirection
 *
 * @returns Object containing openPortal function, loading state, and error state
 */
export const useCustomerPortal = (): UseCustomerPortalReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openPortal = useCallback(async (returnUrl?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const url = returnUrl || window.location.href;
      const { portalUrl } = await createCustomerPortalSession(url);

      // Redirect user to Stripe Customer Portal
      window.location.href = portalUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal. Please try again.');
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    openPortal,
    isLoading,
    error,
    clearError
  };
};

